import { useMemo, useState } from "react";
import {
  ChevronLeft, ChevronRight, X, Plus, AlertTriangle, Lock, Clock, Search,
  CheckCircle2, Ban,
} from "lucide-react";

/**
 * Two calendars, and the difference between them is the whole design.
 *
 * `PlanCalendar` is where a plan is built, and it has **no dates in it at
 * all** - its squares are Day 1, Day 2, Day 3, laid out in the shape of a
 * month so it can be read at a glance. That is what lets one plan serve
 * September 2026, September 2027 and every batch after: nothing in it knows
 * what year it is.
 *
 * `BatchCalendar` is where a plan meets a cohort, and it is a real calendar
 * with real dates. It is **read-only on purpose** - the dates come from the
 * enrolment's start date, weekday pattern and holidays, computed by the
 * server's `working_days`. Letting a tutor drag a square to a different date
 * here would write a date into something, and the next year's batch would
 * inherit it.
 *
 * The grid's arithmetic matches the server's numbering exactly: day numbers
 * run in sequence through the chosen weekdays, so column `c` of week `w` is
 * day `w x weekdays + c + 1`. Holidays shift the *dates* a day number lands
 * on; they never renumber the days. That is why this grid can be drawn
 * without knowing a single date.
 *
 * Which is also why `PlanCalendar`'s columns are headed "1st, 2nd, 3rd" and
 * **not** "Mon, Tue, Wed". A Mon-Fri plan starting 2 November 2026 puts day 8
 * on Wednesday the 11th; make the 11th a holiday for one batch and day 8
 * moves to Thursday the 12th, while staying day 8. The day number is shared
 * by every batch and the weekday is not, so a weekday name over that column
 * would be true of one cohort and a lie about the next - the same mistake as
 * writing a date into the plan, in a form that looks like a label.
 */

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const NTH = ["1st", "2nd", "3rd", "4th", "5th", "6th", "7th"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Monday-first weekday index, because a UK school week starts on Monday. */
const mondayFirst = (jsDay) => (jsDay + 6) % 7;


/** One filter, used by both the square picker and the drag tray.

 * A tutor planning November has already built the worksheets and named them
 * for it, so the useful question is "show me the ones called NOV" rather than
 * "scroll past the other forty". It matches the name and the notes, because a
 * worksheet named "Week 26" may well say November in its note.
 */
function WorksheetFilter({ query, setQuery, kind, setKind, kinds, shown, total }) {
  return (
    <div className="mb-2">
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={query} onChange={(ev) => setQuery(ev.target.value)}
          placeholder="Filter by name — type Nov to see only November's"
          className="w-full border border-slate-300 rounded-lg pl-7 pr-7 py-1.5 text-sm" />
        {query && (
          <button type="button" onClick={() => setQuery("")} title="Clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
            <X size={13} />
          </button>
        )}
      </div>

      {kinds.length > 1 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          <button type="button" onClick={() => setKind("")}
            className={`text-[11px] rounded-full border px-2 py-0.5 capitalize ${
              kind === "" ? "bg-slate-800 text-white border-slate-800"
                : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            All kinds
          </button>
          {kinds.map((k) => (
            <button key={k} type="button" onClick={() => setKind(kind === k ? "" : k)}
              className={`text-[11px] rounded-full border px-2 py-0.5 capitalize ${
                kind === k ? "bg-slate-800 text-white border-slate-800"
                  : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
              {k}
            </button>
          ))}
        </div>
      )}

      {(query.trim() || kind) && (
        <p className="text-[11px] text-slate-500 mt-1">
          Showing <strong>{shown}</strong> of {total} worksheets
          {shown === 0 ? " — nothing matches, so clear the filter to see them all." : ""}
        </p>
      )}
    </div>
  );
}


// ------------------------------------------------------------ building a plan

export function PlanCalendar({ items, weekdays, length, sheets, skipped = [],
                               onAssign, onClear, onMove, onSkip }) {
  const [picking, setPicking] = useState(null);     // the day number being filled
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("");
  const [dragging, setDragging] = useState(null);   // { setId } or { fromDay }

  const cols = useMemo(
    () => [...new Set(weekdays.map(Number))].sort((a, b) => a - b),
    [weekdays]
  );
  const perWeek = cols.length;

  const byId = useMemo(() => {
    const m = {};
    for (const s of sheets) m[s.id] = s;
    return m;
  }, [sheets]);

  /** Day number -> the items sitting on it. A list, so a clash is visible. */
  const onDay = useMemo(() => {
    const m = {};
    for (const it of items) {
      const d = Math.max(1, Number(it.day) || 1);
      (m[d] = m[d] || []).push(it);
    }
    return m;
  }, [items]);

  const clashes = Object.keys(onDay)
    .filter((d) => onDay[d].length > 1)
    .map(Number)
    .sort((a, b) => a - b);

  const maxDay = items.length
    ? Math.max(...items.map((i) => Math.max(1, Number(i.day) || 1)))
    : 0;
  // A stated length is the plan's own answer to "how many days is this?", so
  // the grid draws exactly that many squares and stops. Only a plan with no
  // length gets a spare week on the end to grow into.
  const stated = Math.max(0, Number(length) || 0);
  const span = stated ? Math.max(stated, maxDay) : maxDay;
  const weeks = perWeek > 0
    ? (stated ? Math.ceil(span / perWeek) : Math.max(4, Math.ceil(span / perWeek) + 1))
    : 0;

  /**
   * Days crossed off on purpose: no worksheet that day, by decision.
   *
   * The tutor builds a month a week at a time, so an empty square usually
   * means "not got to it yet" - which is worth shouting about. A crossed
   * square means "nothing is going out that day", which is worth showing
   * and not worth warning about. Children are unaffected either way: an
   * empty day and a crossed day are both days they were given no work, and
   * neither counts against their month.
   */
  const crossed = useMemo(() => new Set((skipped || []).map(Number)), [skipped]);

  // The gaps, which are the thing the tutor wants to be unable to miss.
  const gaps = [];
  for (let d = 1; d <= span; d += 1) if (!onDay[d] && !crossed.has(d)) gaps.push(d);

  const kinds = useMemo(
    () => [...new Set(sheets.map((s) => s.kind).filter(Boolean))].sort(),
    [sheets]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sheets.filter((s) => {
      if (kind && s.kind !== kind) return false;
      if (!q) return true;
      return `${s.title || ""} ${s.notes || ""}`.toLowerCase().includes(q);
    });
  }, [sheets, query, kind]);

  if (perWeek === 0) {
    return (
      <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3.5 py-2.5">
        Pick at least one working day above and the calendar will appear.
      </p>
    );
  }

  const drop = (day) => {
    if (!dragging) return;
    if (dragging.setId) onAssign(day, dragging.setId);
    else if (dragging.fromDay && dragging.fromDay !== day) onMove(dragging.fromDay, day);
    setDragging(null);
  };

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <p className="text-sm text-slate-600">
          The plan —{" "}
          <strong>
            {items.length} worksheet{items.length === 1 ? "" : "s"}
            {stated ? ` of ${span} days` : ""}
          </strong>
          <span className="text-slate-400"> · {perWeek} per week</span>
        </p>
        <p className="text-xs text-slate-400">
          Click a square to fill it, or drag a worksheet onto it
        </p>
      </div>

      {stated > 0 && (
        gaps.length === 0 ? (
          <p className="mb-2 text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 inline-flex items-center gap-1.5">
            <CheckCircle2 size={13} className="shrink-0" />
            {crossed.size > 0
              ? `All ${span} days are decided — ${span - crossed.size} with a worksheet, ${crossed.size} left blank on purpose.`
              : `All ${span} days have a worksheet. Nothing is empty.`}
          </p>
        ) : (
          <p className="mb-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-start gap-1.5">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <span>
              <strong>{gaps.length} of {span} days {gaps.length === 1 ? "is" : "are"} still
              empty</strong> — day {gaps.slice(0, 12).join(", ")}
              {gaps.length > 12 ? ` and ${gaps.length - 12} more` : ""}. A child gets nothing on
              an empty day.
            </span>
          </p>
        )
      )}

      <p className="text-xs text-slate-500 mb-2">
        Each square is the <strong>1st to {NTH[perWeek - 1]} working day</strong> of that week,
        on {cols.map((n) => DAY_NAMES[n]).join("/")}. The real dates are set per batch, so a
        batch with a holiday in week two keeps the same day numbers on later dates.
      </p>

      <div className="overflow-x-auto">
        <div className="min-w-[520px]">
          <div className="grid gap-1 mb-1"
            style={{ gridTemplateColumns: `3.5rem repeat(${perWeek}, minmax(0, 1fr))` }}>
            <span />
            {cols.map((n, c) => (
              <span key={n}
                className="text-[11px] uppercase tracking-wider font-bold text-slate-400 text-center">
                {NTH[c]}
              </span>
            ))}
          </div>

          {Array.from({ length: weeks }, (_, w) => (
            <div key={w} className="grid gap-1 mb-1"
              style={{ gridTemplateColumns: `3.5rem repeat(${perWeek}, minmax(0, 1fr))` }}>
              <span className="text-[11px] font-bold text-slate-400 self-center">
                Week {w + 1}
              </span>
              {cols.map((_, c) => {
                const day = w * perWeek + c + 1;
                if (stated && day > span) {
                  return <span key={c} className="min-h-[62px]" />;
                }
                const here = onDay[day] || [];
                const first = here[0];
                const isCrossed = crossed.has(day);
                const sheet = first ? byId[first.set_id] : null;
                const count = sheet
                  ? (sheet.question_count ?? sheet.question_codes?.length ?? 0)
                  : 0;
                return (
                  <button key={c} type="button"
                    draggable={!!first}
                    onDragStart={() => first && setDragging({ fromDay: day })}
                    onDragEnd={() => setDragging(null)}
                    onDragOver={(ev) => ev.preventDefault()}
                    onDrop={(ev) => { ev.preventDefault(); drop(day); }}
                    onClick={() => { setPicking(day); setQuery(""); }}
                    className={`text-left rounded-lg border px-2 py-1.5 min-h-[62px] transition ${
                      picking === day
                        ? "border-slate-800 ring-2 ring-slate-800/20 bg-white"
                        : isCrossed
                          ? "border-slate-200 bg-slate-100/70 hover:bg-slate-100"
                          : first
                            ? "border-slate-300 bg-white hover:border-slate-500"
                            : "border-dashed border-slate-300 bg-slate-50/60 hover:bg-white"}`}>
                    <span className="block text-[10px] font-bold text-slate-400">
                      Day {day}
                    </span>
                    {isCrossed ? (
                      <span className="flex items-center gap-1 text-[11px] text-slate-500 mt-1">
                        <Ban size={12} className="shrink-0" /> No worksheet
                      </span>
                    ) : sheet ? (
                      <>
                        <span className="text-xs font-medium text-slate-800 leading-tight line-clamp-2">
                          {sheet.title}
                        </span>
                        <span className="block text-[10px] text-slate-400 mt-0.5">
                          {count}q{here.length > 1 ? ` · +${here.length - 1} more` : ""}
                          {count === 0 ? " — empty" : ""}
                        </span>
                      </>
                    ) : first ? (
                      <span className="block text-xs text-red-600">(worksheet deleted)</span>
                    ) : (
                      <Plus size={13} className="text-slate-300 mt-1.5" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {clashes.length > 0 && (
        <p className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 inline-flex items-start gap-1.5">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>
            Day {clashes.join(", ")} {clashes.length === 1 ? "has" : "have"} more than one
            worksheet. A day gives out one worksheet, so only the first will reach the
            children — open the square and clear the rest.
          </span>
        </p>
      )}

      {picking !== null && (
        <div className="mt-3 rounded-xl border border-slate-300 bg-white p-3.5 shadow-sm">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-sm font-semibold text-slate-800">
              Day {picking}
              {crossed.has(picking)
                ? " — left blank on purpose"
                : onDay[picking]?.length
                  ? ` — ${byId[onDay[picking][0].set_id]?.title || "(worksheet deleted)"}`
                  : " — empty"}
            </p>
            <button type="button" onClick={() => setPicking(null)}
              className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
          </div>

          <div className="flex flex-wrap gap-2 mb-2.5">
            {onDay[picking]?.length > 0 && (
              <button type="button"
                onClick={() => { onClear(picking); setPicking(null); }}
                className="text-xs font-semibold text-red-700 border border-red-200 bg-red-50 rounded-lg px-2.5 py-1.5 hover:bg-red-100">
                Clear this day
              </button>
            )}
            {onSkip && (
              <button type="button"
                onClick={() => { onSkip(picking, !crossed.has(picking)); setPicking(null); }}
                className="text-xs font-semibold text-slate-700 border border-slate-300 bg-slate-50 rounded-lg px-2.5 py-1.5 hover:bg-slate-100 inline-flex items-center gap-1.5">
                <Ban size={12} />
                {crossed.has(picking) ? "Put this day back" : "No worksheet this day"}
              </button>
            )}
          </div>

          <WorksheetFilter query={query} setQuery={setQuery} kind={kind} setKind={setKind}
            kinds={kinds} shown={filtered.length} total={sheets.length} />

          <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto">
            {filtered.length === 0 && (
              <p className="text-sm text-slate-400">
                {sheets.length === 0
                  ? "No worksheets yet — build them on the Worksheets page first."
                  : "Nothing matches that filter."}
              </p>
            )}
            {filtered.map((s) => (
              <button key={s.id} type="button"
                onClick={() => { onAssign(picking, s.id); setPicking(null); }}
                className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
                <Plus size={11} /> {s.title}
                <span className="text-slate-400">· {s.question_count ?? 0}q</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-slate-500 mt-3 mb-1.5">
        Your worksheets — drag one onto a square
      </p>
      <WorksheetFilter query={query} setQuery={setQuery} kind={kind} setKind={setKind}
        kinds={kinds} shown={filtered.length} total={sheets.length} />
      <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
        {sheets.length === 0 && (
          <p className="text-sm text-slate-400">
            No worksheets yet — build them on the Worksheets page first.
          </p>
        )}
        {sheets.length > 0 && filtered.length === 0 && (
          <p className="text-sm text-slate-400">Nothing matches that filter.</p>
        )}
        {filtered.map((s) => (
          <span key={s.id} draggable
            onDragStart={() => setDragging({ setId: s.id })}
            onDragEnd={() => setDragging(null)}
            className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 bg-white cursor-grab active:cursor-grabbing select-none">
            {s.title}
            <span className="text-slate-400"> · {s.question_count ?? 0}q</span>
          </span>
        ))}
      </div>
    </div>
  );
}


// ------------------------------------------------------- one batch, real dates

export function BatchCalendar({ calendar, schedule, today }) {
  const cells = useMemo(() => {
    const m = {};
    for (const c of calendar || []) m[c.date] = c;
    return m;
  }, [calendar]);

  const rows = useMemo(() => {
    const m = {};
    for (const r of schedule || []) m[r.day] = r;
    return m;
  }, [schedule]);

  /** The months the programme actually touches - no empty month either side. */
  const months = useMemo(() => {
    const seen = [];
    for (const c of calendar || []) {
      const key = c.date.slice(0, 7);
      if (!seen.includes(key)) seen.push(key);
    }
    return seen;
  }, [calendar]);

  // Open on the month today sits in, which is the one a tutor wants to see;
  // the first month otherwise, for a batch that has not started.
  const initial = Math.max(0, months.indexOf((today || "").slice(0, 7)));
  const [at, setAt] = useState(initial);
  const key = months[Math.min(at, months.length - 1)];

  if (months.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        Nothing is dated yet — the plan has no worksheets in it.
      </p>
    );
  }

  const [yy, mm] = key.split("-").map(Number);
  const firstOfMonth = new Date(Date.UTC(yy, mm - 1, 1));
  const daysInMonth = new Date(Date.UTC(yy, mm, 0)).getUTCDate();
  const lead = mondayFirst(firstOfMonth.getUTCDay());

  const squares = [];
  for (let i = 0; i < lead; i += 1) squares.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    squares.push(`${key}-${String(d).padStart(2, "0")}`);
  }
  while (squares.length % 7 !== 0) squares.push(null);

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <button type="button" disabled={at <= 0} onClick={() => setAt(at - 1)}
          className="p-1.5 rounded-lg border border-slate-300 text-slate-600 disabled:opacity-30 hover:bg-slate-50">
          <ChevronLeft size={15} />
        </button>
        <p className="text-sm font-semibold text-slate-800">
          {MONTHS[mm - 1]} {yy}
        </p>
        <button type="button" disabled={at >= months.length - 1} onClick={() => setAt(at + 1)}
          className="p-1.5 rounded-lg border border-slate-300 text-slate-600 disabled:opacity-30 hover:bg-slate-50">
          <ChevronRight size={15} />
        </button>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[520px]">
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAY_NAMES.map((d) => (
              <span key={d}
                className="text-[11px] uppercase tracking-wider font-bold text-slate-400 text-center">
                {d}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {squares.map((iso, i) => {
              if (!iso) return <span key={i} className="min-h-[64px]" />;
              const cell = cells[iso];
              const row = cell?.day != null ? rows[cell.day] : null;
              const isToday = iso === today;
              const dom = Number(iso.slice(8));

              let tone = "border-slate-100 bg-white";
              if (!cell) tone = "border-slate-100 bg-slate-50/40";
              else if (cell.kind === "holiday") tone = "border-amber-200 bg-amber-50";
              else if (cell.kind === "off") tone = "border-slate-100 bg-slate-50/70";
              else if (row?.state === "open") tone = "border-green-300 bg-green-50";
              else if (row?.state === "closed") tone = "border-slate-200 bg-slate-50";
              else if (row) tone = "border-dashed border-slate-300 bg-white";

              return (
                <div key={i}
                  className={`rounded-lg border px-1.5 py-1 min-h-[64px] ${tone} ${
                    isToday ? "ring-2 ring-slate-800/40" : ""}`}>
                  <span className="flex items-baseline justify-between gap-1">
                    <span className={`text-[11px] font-bold ${
                      isToday ? "text-slate-900" : "text-slate-400"}`}>
                      {dom}
                    </span>
                    {cell?.day != null && (
                      <span className="text-[9px] font-bold text-slate-400">
                        d{cell.day}
                      </span>
                    )}
                  </span>
                  {cell?.kind === "holiday" && (
                    <span className="block text-[10px] text-amber-800 mt-0.5">holiday</span>
                  )}
                  {row && (
                    <>
                      <span className="text-[11px] font-medium text-slate-800 leading-tight line-clamp-2 mt-0.5">
                        {row.title}
                      </span>
                      {row.state === "open" && (
                        <span className="flex items-center gap-0.5 text-[9px] font-semibold text-green-700 mt-0.5">
                          <Clock size={8} />
                          {row.days_left > 0 ? `${row.days_left}d left` : "last day"}
                          {row.handed_in > 0 ? ` · ${row.handed_in} in` : ""}
                        </span>
                      )}
                      {row.state === "closed" && (
                        <span className="flex items-center gap-0.5 text-[9px] font-semibold text-slate-500 mt-0.5">
                          <Lock size={8} /> closed
                          {row.handed_in > 0 ? ` · ${row.handed_in} in` : ""}
                        </span>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded border border-green-300 bg-green-50" /> open now
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded border border-slate-200 bg-slate-50" /> closed
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded border border-dashed border-slate-300 bg-white" /> still to come
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded border border-amber-200 bg-amber-50" /> holiday
        </span>
        <span>d4 = day 4 of the plan</span>
      </div>
    </div>
  );
}
